<template src="./shopper-profile-fieldset.html"></template>

<script lang="ts">
import CheckGroup from 'objects/form/CheckGroup.vue'
import FieldGroup from 'objects/form/FieldGroup.vue'
import Selectbox from 'objects/form/Selectbox.vue'
import TextField from 'objects/form/TextField.vue'


const CANADA_COUNTRY_CODE = 'CA'
const US_COUNTRY_CODE = 'US'

export const SINGLE_SELECT_TYPE = 0
export const FREE_FORM_TEXT_TYPE = 1
export const MULTI_SELECT_TYPE = 2

export default {
  data() {
    return {
      FREE_FORM_TEXT_TYPE,
      MULTI_SELECT_TYPE,
      SINGLE_SELECT_TYPE,
      initialValues: null,
      // local copy of the values. Will be updated with 'values' prop
      profileValues: {
        ageRange: null,
        ageRangeIsDirty: false,
        country: null,
        state: null,
        city: null,
        locationIsDirty: false,
        customAttributes: []
      }
    }
  },
  props: {
    ageRange: Object, // ShopperProfileDefinition.ageRange
    location: Object, // ShopperProfileDefinition.location
    customAttributes: Array, // ShopperProfileDefinition.customAttributes
    // property name is id of attr, value is error message string
    profileErrors: Object,
    modelValue: Object // actual values of all the fields
  },
  emits: ['update:modelValue'],
  components: {
    'tt-check-group': CheckGroup,
    'tt-field-group': FieldGroup,
    'tt-selectbox': Selectbox,
    'tt-text-field': TextField
  },
  computed: {
    ageRanges() {
      return this.ageRange ? this.convertToSelectOptions(this.ageRange.ageRanges) : []
    },
    countries() {
      return this.location ? this.convertToSelectOptions(this.location.countries) : []
    },
    country() {
      return this.profileValues.country
    },
    customAttributeValueDefinitions() {
      const valueDefs = {}
      if (this.customAttributes) {
        this.customAttributes.forEach((customAttr) => {
          if (customAttr.type === SINGLE_SELECT_TYPE || customAttr.type === MULTI_SELECT_TYPE) {
            valueDefs[customAttr.id] = customAttr.valueDefinitions.map((valueDef) => {
              return { value: valueDef.id, label: valueDef.label }
            })
          }
        })
      }
      return valueDefs
    },
    modelValues() {
      // Same object as this.profileValues being sent, except that the customAttributes
      // are sent in an array instead of an object
      return  {
        ageRange: this.profileValues.ageRange,
        country: this.profileValues.country,
        state: this.profileValues.state,
        city: this.profileValues.city,
        customAttributes: this.profileValues.customAttributes ?
          Object.keys(this.profileValues.customAttributes).map((id) => {
            return { id, value: this.profileValues.customAttributes[id] }
          }) : []
      }
    },
    showCustomAttributes() {
      if (this.customAttributes && this.profileValues.customAttributes) {
        return this.customAttributes.length > 0 &&
          this.customAttributes.length === this.profileValues.customAttributes.length
      }
      return false
    },
    showProfileAttributes() {
      return this.ageRange || this.location || this.showCustomAttributes
    },
    stateOptions() {
      const countryCode = this.profileValues.country
      if (countryCode === US_COUNTRY_CODE) {
        return this.convertToSelectOptions(this.location.usStates)
      } else if (countryCode === CANADA_COUNTRY_CODE) {
        return this.convertToSelectOptions(this.location.caProvinces)
      }
      return null
    },
    stateOrProvinceLabel() {
      const countryCode = this.profileValues.country
      return this.$msg(`profileForm.${countryCode === CANADA_COUNTRY_CODE ? 'province' : 'state' }`)
    }
  },
  methods: {
    convertToSelectOptions(obj) {
      return obj ? Object.keys(obj).map((key) => { return { label: obj[key], value: key } }) : []
    },
    nullIfEmpty(value) {
      return typeof value === 'string' && value.trim() === '' ? null : value
    },
    setProfileValues() {
      if (this.modelValue && Object.keys(this.modelValue).length > 0) {
        let country = this.modelValue.country
        if (this.initialValues === null) {
          this.initialValues = JSON.parse(JSON.stringify(this.modelValue))
          // this is the first time setting values, set the defaultCountry if present
          const defaultCountry = this.location ? this.location.defaultCountry : null
          if (defaultCountry && !country) {
            country = defaultCountry
            // this.locationIsDirty = true
          }
        }
        const customAttributes = this.modelValue.customAttributes || []
        Object.assign(this.profileValues, this.modelValue, { country, customAttributes })
      }
      // make sure there is a value for each attribute.
      // Won't be present if user hasn't answered before
      const attrValues = this.profileValues.customAttributes
      if (this.customAttributes && this.customAttributes.length > attrValues.length) {
        this.profileValues.customAttributes = this.customAttributes.map((attrDef) => {
          const matchingValue = attrValues.find(val => attrDef.id === val.id)
          const valueDefinitionId =
            attrDef.type === FREE_FORM_TEXT_TYPE ? attrDef.valueDefinitions[0].id : null
          return matchingValue || { id: attrDef.id, valueDefinitionId, freeFormText: null }
        })
      }
    }
  },
  watch: {
    country(newVal, oldVal) {
      // if the country changes, clear out the state. Sometimes it may be correct, but more
      // likely it is not which cannot be seen when switching to the drop-down (US or CA)
      // only want to clear if country was already set in the first place.  This is to avoid
      // the initial value being set causing it to clear
      if (oldVal) {
        this.profileValues.state = ''
      }
    },
    profileValues: {
      handler() {
        if (!!this.ageRange && this.initialValues) {
          // IBS flow - The reviewer comes from a  if ageRangeIsDirty is passed as true,
          // it should remain true, since we dont know the value from before.
          this.profileValues.ageRangeIsDirty = this.initialValues.ageRangeIsDirty
            || this.profileValues.ageRange !== this.initialValues.ageRange
        }
        if (this.location && this.initialValues) {
          this.profileValues.locationIsDirty =
            this.nullIfEmpty(this.profileValues.country) !== this.initialValues.country
            || this.nullIfEmpty(this.profileValues.state) !== this.initialValues.state
            || this.nullIfEmpty(this.profileValues.city) !== this.initialValues.city
        }
        this.$emit('update:modelValue', this.profileValues)
      },
      deep: true
    }
  },
  created() {
    // the 'values' watcher is not fired on initial creation
    this.setProfileValues()
  }
}
</script>

<style lang="scss" src="./shopper-profile-fieldset.scss"></style>
